// Part 1a rows: the one row assembler (buildRow, decision D33) and the NL pre-fill
// (prefillRows, routeAndPrefill). Every value the runners used to derive from the spec at
// dispatch (MatterGen properties and guidance, FM4M filters and featurization model, the
// GT4SD RT pair) is written into row.params here, once, at Route.
//
// Pure and deterministic: no React, no I/O, no Date or randomness, and nothing here mutates
// `calls`, `spec` or a row it is given (routeSpec passes spec.targets and elements_in by
// reference). The router stays LLM-free.
import {
  GT4SD_CONDITIONABLE_PROPS,
  routeSpec,
  usableConditioningValue,
  mattergenConditioningValue,
  variantConditioningProperty,
} from '../router/modelRouter';
import { chargeOf, fitRunTotal, minCandidatesFor } from '../router/fitRunTotal';
import { normalizeModelPolicy } from '../../config/capabilities';
import { PLAN_LIMITS } from '../../config/planLimits';
import { GT4SD_CHECKPOINTS, GT4SD_RT_TARGET_RANGE, gt4sdTargetToken } from '../../config/gt4sdCheckpoints';
import {
  MATTERGEN_CHECKPOINT_CONDITIONING,
  MATTERGEN_REQUIRED_CONDITIONING,
  MATTERGEN_REQUIRED_CONDITIONING_FALLBACK,
  mattergenPropertyError,
  mattergenChemicalSystem,
  customMattergenCheckpoint,
} from '../../config/mattergenConditioning';
import { DEFAULT_MATTERGEN_CHUNK_SIZE } from '../../components/mattergen/mattergenJobs';
import { selectCustomModel } from '../../context/customModelSelection';
import { fm4mSeedSmiles } from '../../utils/generationBounds';
import {
  SERVICE_DOMAINS,
  MAX_ROW_CANDIDATES,
  FM4M_REPRESENTATION_MODELS,
  FM4M_FEATURIZE_CHUNK,
  GT4SD_ALGORITHMS,
  ROW_KINDS,
  ROW_SERVICES,
  cloneRow,
  newRowId,
  rowPriority,
  runTotalItemForRow,
} from './rowContract';

const own = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
const isPlainObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

// Seed bounds of fm4m_represent.seed_smiles (rowContract PARAM_ALLOWLIST): at most 1000
// seeds, each 1 to 1000 characters after trimming.
const MAX_SEEDS = 1000;
const MAX_SEED_LENGTH = 1000;
// buildRow defaults (1a-contracts 1.2): FM4M max_length is the service default
// (ibm-materials-service main.py), oversample 3 only with filters, guidance 2.0 only with
// properties (0 would silently disable conditioning).
const FM4M_DEFAULT_MAX_LENGTH = 256;
const FM4M_FILTERED_OVERSAMPLE = 3;
const MATTERGEN_DEFAULT_GUIDANCE = 2.0;

// Spec target name (lowercase) -> RDKit filter key. Moved from the router's FM4M
// buildDispatchSpec (modelRouter.js at 7f0c897), which 1a-03 deletes.
const FM4M_TARGET_FILTER_KEYS = Object.freeze({
  molecular_weight: 'molecular_weight', logp: 'logP', qed: 'qed',
  sa_score: 'sa_score', hbd: 'hbd', hba: 'hba', tpsa: 'tpsa',
  ro5_violations: 'ro5_violations', rotatable_bonds: 'rotatable_bonds',
  ring_count: 'ring_count', aromatic_rings: 'aromatic_rings',
  heavy_atoms: 'heavy_atoms', fraction_sp3: 'fraction_sp3',
});

/** A candidate count as an integer from 1 to MAX_ROW_CANDIDATES (a non-number counts as 1). */
function clampCount(n) {
  const v = Math.floor(Number(n));
  if (!Number.isFinite(v)) return 1;
  return Math.min(MAX_ROW_CANDIDATES, Math.max(1, v));
}

function customModelIdOrNull(v) {
  return typeof v === 'string' && v.length >= 1 && v.length <= 128 ? v : null;
}

function isBuiltinCheckpoint(name) {
  return typeof name === 'string' && own(MATTERGEN_CHECKPOINT_CONDITIONING, name);
}

/** The FM4M alias rule: a representation model stays; the generative aliases bart, mhg and
 * smi-ted and any unknown name become mol-xl (the runner's own remap at 7f0c897). */
function fm4mModelOrDefault(name) {
  return FM4M_REPRESENTATION_MODELS.includes(name) ? name : 'mol-xl';
}

function defaultSteering() {
  return { derivedFrom: [], values: {} };
}

/** Usable seeds: strings only, trimmed, empty and over-1000-character entries dropped, at most
 * 1000 kept. A single string counts as a one-seed list. Every represent-or-generate decision
 * (prefillRows, manualFormToRows, buildRow, 1b) uses rowSeedSmiles(...).length > 0. */
export function rowSeedSmiles(raw) {
  const list = Array.isArray(raw) ? raw : (typeof raw === 'string' ? [raw] : []);
  const out = [];
  for (const s of list) {
    if (typeof s !== 'string') continue;
    const t = s.trim();
    if (t.length === 0 || t.length > MAX_SEED_LENGTH) continue;
    out.push(t);
    if (out.length === MAX_SEEDS) break;
  }
  return out;
}

/** A filter bound as a finite number, else undefined. null, '', booleans, arrays and text are
 * dropped (Number() would turn null, '' and [] into 0); a numeric string becomes a number. */
function finiteBound(v) {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean' || Array.isArray(v)) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * FM4M post-generation filters from the spec targets (the router's FM4M buildDispatchSpec
 * derivation, with coercion so the output validates). '>=' and '>' set `min`, '<=' and '<'
 * set `max`, and 'range' with an array of at least 2 sets `min` and `max`; each bound merges
 * into that key's filter and is kept only when finite. Afterwards a filter with min > max or
 * no bound is dropped. Other constraints ('=', 'maximize', ...) give no filter.
 */
export function fm4mFiltersFromTargets(targets) {
  const filters = {};
  const merge = (key, bounds) => {
    if (Object.keys(bounds).length > 0) filters[key] = { ...(filters[key] || {}), ...bounds };
  };
  for (const t of Array.isArray(targets) ? targets : []) {
    const name = String(t?.name || '').toLowerCase();
    if (!own(FM4M_TARGET_FILTER_KEYS, name)) continue;
    const key = FM4M_TARGET_FILTER_KEYS[name];
    const c = t?.constraint;
    if (c === '>=' || c === '>') {
      const min = finiteBound(t.value);
      merge(key, min === undefined ? {} : { min });
    } else if (c === '<=' || c === '<') {
      const max = finiteBound(t.value);
      merge(key, max === undefined ? {} : { max });
    } else if (c === 'range' && Array.isArray(t.value) && t.value.length >= 2) {
      const min = finiteBound(t.value[0]);
      const max = finiteBound(t.value[1]);
      merge(key, { ...(min === undefined ? {} : { min }), ...(max === undefined ? {} : { max }) });
    }
  }
  for (const key of Object.keys(filters)) {
    const f = filters[key];
    const hasMin = f.min !== undefined;
    const hasMax = f.max !== undefined;
    if ((!hasMin && !hasMax) || (hasMin && hasMax && f.min > f.max)) delete filters[key];
  }
  return filters;
}

/**
 * The GT4SD Regression Transformer pair, { target_property, target_value } or {}. It uses the
 * first target in spec order whose lowercase name is GT4SD-conditionable and whose
 * usableConditioningValue is defined (M1: a midpoint for a numeric range, nothing for a
 * valueless target). The property is the exact wire token gt4sdTargetToken returns for
 * `algorithmVersion` ('logp'); a null token gives {}.
 */
export function gt4sdConditioningFromTargets(targets, algorithmVersion) {
  // The value has to be one the checkpoint accepts, on the same test selectGT4SDVariant
  // uses to decide whether to route here at all. Taking the first USABLE target instead
  // of the first ACCEPTABLE one meant a spec carrying logP 15 and logP 2.5 routed to RT
  // on the strength of the 2.5 and then prefilled the 15, giving a row the service would
  // have refused. With no acceptable target this returns {} and the row is refused
  // locally on target_value, which is the 1.1 carve-out.
  const [low, high] = GT4SD_RT_TARGET_RANGE;
  const t = (Array.isArray(targets) ? targets : []).find((x) => {
    if (!GT4SD_CONDITIONABLE_PROPS.has(String(x?.name || '').toLowerCase())) return false;
    const value = usableConditioningValue(x?.value);
    return value !== undefined && value >= low && value <= high;
  });
  if (!t) return {};
  const token = gt4sdTargetToken(String(t.name), algorithmVersion);
  if (!token) return {};
  return { target_property: token, target_value: usableConditioningValue(t.value) };
}

/**
 * MatterGen `properties` derived from the spec for one built-in checkpoint: for each key that
 * checkpoint conditions on, chemical_system comes from system.elements_in
 * (mattergenChemicalSystem: known, unique symbols, at most 30) and every other key from
 * mattergenConditioningValue(key, spec.targets). Undefined values are omitted, so elements
 * never reach a checkpoint without a chemical_system embedding (B1). An unknown checkpoint
 * gives {}.
 */
export function mattergenPropertiesFor(target, spec) {
  if (!isBuiltinCheckpoint(target)) return {};
  const s = isPlainObject(spec) ? spec : {};
  const elements = Array.isArray(s.system?.elements_in) ? s.system.elements_in : [];
  const out = {};
  for (const key of MATTERGEN_CHECKPOINT_CONDITIONING[target]) {
    const v = key === 'chemical_system'
      ? mattergenChemicalSystem(elements)
      : mattergenConditioningValue(key, s.targets);
    if (v !== undefined) out[key] = v;
  }
  return out;
}

/** Spec-derived properties with the expert's values merged over them, restricted to the
 * checkpoint's keys and to values that pass mattergenPropertyError (any other expert entry is
 * dropped). */
function mergedMattergenProperties(target, spec, expertRaw) {
  const merged = mattergenPropertiesFor(target, spec);
  if (isPlainObject(expertRaw)) {
    for (const key of MATTERGEN_CHECKPOINT_CONDITIONING[target]) {
      if (own(expertRaw, key) && mattergenPropertyError(key, expertRaw[key]) === null) merged[key] = expertRaw[key];
    }
  }
  return merged;
}

function mattergenRow(f, spec) {
  let target = isBuiltinCheckpoint(f.target) ? f.target : 'mattergen_base';
  let properties = mergedMattergenProperties(target, spec, f.properties);
  // Required pair (D38): HHI without both values is a duplicate dft_mag_density run, so an
  // incomplete pair falls back, after the expert values are merged in.
  const required = own(MATTERGEN_REQUIRED_CONDITIONING, target) ? MATTERGEN_REQUIRED_CONDITIONING[target] : null;
  if (required && !required.every((key) => properties[key] !== undefined)) {
    target = MATTERGEN_REQUIRED_CONDITIONING_FALLBACK[target];
    properties = mergedMattergenProperties(target, spec, f.properties);
  }
  const params = { properties };
  if (Object.keys(properties).length > 0) params.diffusion_guidance_factor = MATTERGEN_DEFAULT_GUIDANCE;
  params.record_trajectories = true;
  return {
    id: f.id,
    service: 'mattergen',
    domain: 'inorganic_crystal',
    kind: 'generate',
    target,
    customModelId: customModelIdOrNull(f.customModelId),
    candidates: clampCount(f.candidates),
    chunkSize: DEFAULT_MATTERGEN_CHUNK_SIZE,
    params,
    steering: defaultSteering(),
    origin: f.origin,
    dispatchTarget: null,
    adjustments: {},
  };
}

function fm4mRow(f, spec) {
  const domain = SERVICE_DOMAINS.fm4m.includes(f.domain) ? f.domain : 'small_molecule';
  const model = fm4mModelOrDefault(f.target);
  const common = {
    id: f.id,
    service: 'fm4m',
    domain,
    chunkSize: FM4M_FEATURIZE_CHUNK.default,
    steering: defaultSteering(),
    origin: f.origin,
    dispatchTarget: null,
    adjustments: {},
  };
  if (f.kind === 'represent') {
    const seeds = rowSeedSmiles(f.seedSmiles ?? fm4mSeedSmiles(spec.meta));
    if (seeds.length > 0) {
      return {
        ...common,
        kind: 'represent',
        target: model,
        customModelId: null,
        candidates: seeds.length,
        params: { seed_smiles: seeds, return_mode: 'file' },
      };
    }
    // No usable seeds: callers never ask for this, but if one does, the generate row for the
    // same fields keeps the builder invariant (featurization_model = the requested model).
  }
  const filters = fm4mFiltersFromTargets(spec.targets);
  return {
    ...common,
    kind: 'generate',
    target: 'bart',
    customModelId: customModelIdOrNull(f.customModelId),
    candidates: clampCount(f.candidates),
    params: {
      filters,
      oversample_multiplier: Object.keys(filters).length > 0 ? FM4M_FILTERED_OVERSAMPLE : 1,
      max_length: FM4M_DEFAULT_MAX_LENGTH,
      featurization_model: model,
      return_mode: 'file',
    },
  };
}

function gt4sdRow(f, spec) {
  // Part D: the fallback is regression_transformer, the only algorithm with a built-in
  // checkpoint (RT-6 removed hugging_face's). Falling back to hugging_face would build a
  // row that prepareRows must then reject as model_unavailable.
  const target = GT4SD_ALGORITHMS.includes(f.target) ? f.target : 'regression_transformer';
  const versions = GT4SD_CHECKPOINTS[target];
  const params = {};
  // The 1.1 exception (live data): an algorithm whose checkpoint list is missing or empty gets
  // no algorithm_version. The row then fails validateRowParams on that key only, and
  // prepareRows (1a-03) rejects it as 'model_unavailable'. Never index an empty list.
  if (Array.isArray(versions) && versions.length > 0) params.algorithm_version = versions[0];
  if (target === 'regression_transformer') {
    Object.assign(params, gt4sdConditioningFromTargets(spec.targets, params.algorithm_version));
  }
  return {
    id: f.id,
    service: 'gt4sd',
    domain: 'small_molecule',
    kind: 'generate',
    target,
    customModelId: customModelIdOrNull(f.customModelId),
    candidates: clampCount(f.candidates),
    chunkSize: null,
    params,
    steering: defaultSteering(),
    origin: f.origin,
    dispatchTarget: null,
    adjustments: {},
  };
}

/**
 * The one row assembler (D33). Owns the TARGETS mapping, every buildRow default, the
 * properties, filters and RT conditioning derived from the spec, the default steering and
 * adjustments. The caller decides service, kind, domain, target and customModelId (a custom
 * MatterGen target is customMattergenCheckpoint(model)); buildRow never selects a custom model.
 * @param {{ id, service, kind, domain, target, customModelId, candidates, origin,
 *           seedSmiles?, properties? }} fields
 *   It reads only these ten keys and ignores any other (for example a compatibleTargets `label`).
 *   seedSmiles (fm4m represent only): the expert's seeds; when absent, fm4mSeedSmiles(spec.meta).
 *   properties (mattergen only): expert values merged over the spec-derived ones.
 * @param {object} spec  the generation_spec (targets, system.elements_in, meta.seed_smiles). Any of
 *   targets, system and meta may be missing (a manual-mode spec); a missing part derives nothing.
 * @returns {Row} passes validateRowParams (1.1 builder invariant, with the one live-data GT4SD
 *   exception); dispatchTarget null. Counts are clamped into 1 to 1024; an unknown MatterGen
 *   checkpoint becomes mattergen_base, an unknown GT4SD algorithm regression_transformer, an FM4M domain
 *   outside SERVICE_DOMAINS.fm4m small_molecule. Throws only for a service outside
 *   ROW_SERVICES, which no caller passes.
 */
export function buildRow(fields, spec) {
  const f = isPlainObject(fields) ? fields : {};
  const s = isPlainObject(spec) ? spec : {};
  if (f.service === 'mattergen') return mattergenRow(f, s);
  if (f.service === 'fm4m') return fm4mRow(f, s);
  if (f.service === 'gt4sd') return gt4sdRow(f, s);
  throw new TypeError(`buildRow: unknown service ${String(f.service)}`);
}

/** 2a's per-row steering entry point: the identity in 1a (it returns a deep copy). 2a writes
 * params and steering from the targets the row covers. prefillRows step 2, and 1b's swap and
 * Add a step, call it right after buildRow. manualFormToRows never calls it (D33). */
export function applySteering(row, targets) {
  return cloneRow(row);
}

// R4-6 snap step and the plan-max fit (prefill steps 3 and 4).
const MATTERGEN_STEP = 8;
const PRIORITY_RANK = { must: 3, prefer: 2, optional: 1, none: 0 };

/**
 * R4-6 snap of MatterGen counts onto multiples of 8 (spec 2026-10-07, prefill step 3). Only
 * MatterGen rows change; the input is never mutated.
 * - a count c >= 8 becomes floor(c/8)*8 and the dropped part goes to a leftover L;
 * - a count c < 8 becomes 8 (never 0) and adds nothing to L;
 * - floor(L/8)*8 goes to the highest-priority MatterGen row (rowPriority against `targets`;
 *   ties go to the first in row order), never above MAX_ROW_CANDIDATES;
 * - every MatterGen row whose count changed gets adjustments.roundedFrom = its count before
 *   the snap.
 * Example: 22/21/21 becomes 16/16/16 with L = 16, then 32/16/16 when the first row is the
 * highest priority.
 */
export function snapMattergenRows(rows, targets) {
  const out = (Array.isArray(rows) ? rows : []).map((row) => cloneRow(row));
  const before = new Map();
  let leftover = 0;
  out.forEach((row, i) => {
    if (row?.service !== 'mattergen') return;
    const c = Math.floor(Number(row.candidates));
    before.set(i, row.candidates);
    if (c >= MATTERGEN_STEP) {
      const snapped = Math.floor(c / MATTERGEN_STEP) * MATTERGEN_STEP;
      leftover += c - snapped;
      row.candidates = snapped;
    } else {
      row.candidates = MATTERGEN_STEP;
    }
  });
  const bonus = Math.floor(leftover / MATTERGEN_STEP) * MATTERGEN_STEP;
  if (bonus > 0 && before.size > 0) {
    let best = -1;
    for (const i of before.keys()) {
      if (best < 0 || PRIORITY_RANK[rowPriority(out[i], targets)] > PRIORITY_RANK[rowPriority(out[best], targets)]) best = i;
    }
    out[best].candidates = Math.min(MAX_ROW_CANDIDATES, out[best].candidates + bonus);
  }
  for (const [i, original] of before) {
    if (out[i].candidates !== original) out[i].adjustments = { ...(out[i].adjustments || {}), roundedFrom: original };
  }
  return out;
}

/** The custom models selectCustomModel may offer: those whose family the org policy runs
 * (enabled, weight above 0; settled decision 5). selectCustomModel itself keeps only
 * verified models of the asked family. */
function runnableCustomModels(customModels, modelPolicy) {
  const policy = normalizeModelPolicy(modelPolicy);
  return (Array.isArray(customModels) ? customModels : []).filter((m) => {
    const entry = m && typeof m.family === 'string' && own(policy, m.family) ? policy[m.family] : null;
    return !!entry && entry.enabled && entry.weight > 0;
  });
}

/** buildRow fields for router call `index`, or null for a call the rows path cannot serve. */
function fieldsForCall(call, index, spec, offered) {
  if (!isPlainObject(call)) return null;
  const id = `r${index + 1}`;
  const candidates = call.budget;
  if (call.service === 'mattergen') {
    let target = call.variant;
    let customModelId = null;
    const property = variantConditioningProperty('mattergen', target);
    const picked = selectCustomModel({ customModels: offered, family: 'mattergen', property, familyConditions: true });
    if (picked) {
      customModelId = picked;
      target = customMattergenCheckpoint(offered.find((m) => m.id === picked));
    }
    return { id, service: 'mattergen', kind: 'generate', domain: 'inorganic_crystal', target, customModelId, candidates, origin: 'router' };
  }
  if (call.service === 'fm4m') {
    // Seeds reach an NL spec only from pre-C5 legacy plans: sanitizeNlMeta strips seed_smiles.
    if (rowSeedSmiles(fm4mSeedSmiles(spec.meta)).length > 0) {
      return { id, service: 'fm4m', kind: 'represent', domain: call.domain, target: 'mol-xl', customModelId: null, candidates, origin: 'router' };
    }
    const customModelId = selectCustomModel({ customModels: offered, family: 'fm4m', familyConditions: false });
    return { id, service: 'fm4m', kind: 'generate', domain: call.domain, target: 'bart', customModelId, candidates, origin: 'router' };
  }
  if (call.service === 'gt4sd') {
    const customModelId = selectCustomModel({ customModels: offered, family: 'gt4sd', familyConditions: false });
    return { id, service: 'gt4sd', kind: 'generate', domain: 'small_molecule', target: call.variant, customModelId, candidates, origin: 'router' };
  }
  return null;
}


// ── Add a step and Swap (editable-plan 1b-1) ─────────────────────────────────
// Both are pure and both go through buildRow, so a row an expert adds or swaps is
// assembled by exactly the same code as a routed one. The reducer takes the finished row
// and validates it rather than building one itself (D-1b1-1).

/** True for a compatibleTargets option this module can build from. */
function isOption(option) {
  return isPlainObject(option)
    && ROW_SERVICES.includes(option.service)
    && ROW_KINDS.includes(option.kind)
    && typeof option.target === 'string'
    && typeof option.domain === 'string';
}

/**
 * A new row for an added step. The id is the first free `<service><n>`, the origin is
 * 'expert', and the count defaults to the service minimum so a new step fits wherever
 * there is any room at all (the user raises it afterwards).
 *
 * @returns {Row|null} null for an option this module cannot build from.
 */
export function rowForAdd(option, spec, existingRows, overrides) {
  if (!isOption(option)) return null;
  const extra = isPlainObject(overrides) ? overrides : {};
  // A representation step's candidate count IS its seed count, so with no seeds there is
  // no row to build. buildRow would otherwise fall back to the GENERATE row for the same
  // model (a deliberate 1a guard for callers that "never ask for this"), which would
  // quietly hand back a different kind of step than the one that was added. Refuse, and
  // let the caller collect seeds first.
  if (option.kind === 'represent') {
    const seeds = rowSeedSmiles(extra.seedSmiles ?? fm4mSeedSmiles(spec && spec.meta));
    if (seeds.length === 0) return null;
    return applySteering(buildRow({
      id: newRowId(existingRows, option.service),
      service: option.service,
      kind: option.kind,
      domain: option.domain,
      target: option.target,
      customModelId: option.customModelId ?? null,
      candidates: seeds.length,
      origin: 'expert',
      seedSmiles: seeds,
    }, spec), spec && spec.targets);
  }
  return applySteering(buildRow({
    id: newRowId(existingRows, option.service),
    service: option.service,
    kind: option.kind,
    domain: option.domain,
    target: option.target,
    customModelId: option.customModelId ?? null,
    candidates: extra.candidates === undefined ? minCandidatesFor(option.service) : extra.candidates,
    origin: 'expert',
  }, spec), spec && spec.targets);
}

/**
 * The row a swap replaces `row` with: same id, same count, new identity, params rebuilt
 * from the spec for the new target (D-1b1-4). An expert param override is NOT carried
 * across, because it belonged to the target being replaced.
 *
 * A represent row keeps its OWN seeds: buildRow otherwise falls back to the spec meta
 * seeds, which would replace what the expert typed with whatever the request carried.
 *
 * @returns {Row|null} null for a malformed row or option.
 */
export function rowForSwap(row, option, spec) {
  if (!isPlainObject(row) || !isOption(option)) return null;
  // Same refusal as rowForAdd: with no seeds there is no representation row, and buildRow
  // would fall back to the GENERATE row for the same model. compatibleTargets will not
  // offer a cross-kind swap today (D-1b1-5), so this guards a future caller rather than a
  // live path, but the silent change of kind is the same either way.
  let seeds;
  if (option.kind === 'represent') {
    seeds = rowSeedSmiles(isPlainObject(row.params) ? row.params.seed_smiles : undefined);
    if (seeds.length === 0) return null;
  }
  return applySteering(buildRow({
    id: row.id,
    service: option.service,
    kind: option.kind,
    domain: option.domain,
    target: option.target,
    customModelId: option.customModelId ?? null,
    candidates: row.candidates,
    origin: 'expert',
    ...(seeds === undefined ? {} : { seedSmiles: seeds }),
  }, spec), spec && spec.targets);
}

/** Prefill step 4: fitRunTotal over the rows. When it scales, fitted counts are written back
 * with adjustments.scaledFrom = the pre-fit count, and a row whose positive charge was fitted
 * to 0 is removed (routeAndPrefill reports it as plan_max).
 *
 * Exported for the review reducer (1b-1), which applies the same fit after a row edit, so
 * there is one place that decides a fitted count and writes adjustments.scaledFrom. The
 * reducer treats a DROPPED row differently: at Route a dropped row is reported as
 * plan_max, but in step 2 a row the user just added must not silently vanish, so the
 * reducer refuses the whole action instead (D-1b1-2). */
export function fitRows(rows, targets, planMax) {
  const items = rows.map((row) => runTotalItemForRow(row, targets));
  const fit = fitRunTotal(items, planMax);
  if (!fit.scaled) return rows;
  const out = [];
  rows.forEach((row, i) => {
    const fitted = fit.items[i].budget;
    if (chargeOf(items[i]) > 0 && fitted <= 0) return;
    if (fitted === row.candidates) {
      out.push(row);
      return;
    }
    // Keep the FIRST count this row was scaled from. The "scaled to fit" label names
    // where the count came from, and overwriting it with the previous fitted figure made
    // the label stop naming the router's number after a second edit.
    const scaledFrom = typeof row.adjustments.scaledFrom === 'number' ? row.adjustments.scaledFrom : row.candidates;
    out.push({ ...row, candidates: fitted, adjustments: { ...row.adjustments, scaledFrom } });
  });
  return out;
}

/**
 * Router calls to rows, in this order: base rows (buildRow), steering (applySteering, the
 * identity until 2a), the R4-6 snap (snapMattergenRows), then the fit (fitRunTotal).
 * @param {RouterCall[]} calls  routeSpec(spec, { modelPolicy }).calls, budgets already org-weighted
 * @param {object} spec         the generation_spec the calls were routed from
 * @param {{ customModels: object[], modelPolicy: object, planMax: number }} opts
 *   customModels: the org's verified models, NOT policy-filtered (filtered here);
 *   modelPolicy: the raw org policy (normalized inside with normalizeModelPolicy);
 *   planMax: planLimits.maxCandidatesPerRun (missing -> PLAN_LIMITS.pilot.maxCandidatesPerRun).
 * @returns {Row[]} rows in call order, ids r<callIndex+1>, origin 'router', dispatchTarget null.
 *   A call whose fitted budget is 0 has no row (its id is skipped).
 * Reads only service, variant, budget and domain from each call, and targets,
 * system.elements_in and meta.seed_smiles from the spec. Target names match by lowercase
 * exact name, never by substring (B15).
 */
export function prefillRows(calls, spec, opts = {}) {
  const s = isPlainObject(spec) ? spec : {};
  const o = isPlainObject(opts) ? opts : {};
  const planMax = Number(o.planMax) || PLAN_LIMITS.pilot.maxCandidatesPerRun;
  const offered = runnableCustomModels(o.customModels, o.modelPolicy);
  const base = [];
  (Array.isArray(calls) ? calls : []).forEach((call, i) => {
    const fields = fieldsForCall(call, i, s, offered);
    if (fields) base.push(applySteering(buildRow(fields, s), s.targets));
  });
  return fitRows(snapMattergenRows(base, s.targets), s.targets, planMax);
}

/**
 * Route + prefill, plus the notes the review needs. budgetSplit is retired: always 'full'.
 * @returns {{ calls, rows, skipped }} skipped = routeSpec's skipped entries
 *   ({ service, domain, reason: 'org_policy'|'zero_weight', fallback? }) followed by
 *   { service, domain, reason: 'plan_max' } for every call with no row.
 */
export function routeAndPrefill(spec, { modelPolicy, customModels, planMax } = {}) {
  const { calls, skipped = [] } = routeSpec(spec, { modelPolicy });
  const rows = prefillRows(calls, spec, { customModels, modelPolicy, planMax });
  const ids = new Set(rows.map((row) => row.id));
  const planMaxSkipped = [];
  calls.forEach((call, i) => {
    if (ids.has(`r${i + 1}`)) return;
    planMaxSkipped.push({ service: call.service, domain: call.service === 'mattergen' ? 'inorganic_crystal' : call.domain, reason: 'plan_max' });
  });
  return { calls, rows, skipped: [...skipped.map((entry) => ({ ...entry })), ...planMaxSkipped] };
}
